import { isDefined } from "@clipboard-health/util-ts";
import { useGetQuery, type UseGetQueryOptions } from "@src/appV2/api";
import {
  useInfiniteGetParsedQuery,
  type UseInfiniteGetParsedQueryOptions,
} from "@src/appV2/api/useInfiniteGetParsedQuery";
import {
  type ChartingNoncomplianceState,
  type GetChartingCompletionReportsResponse,
  GetChartingCompletionReportsResponseSchema,
} from "@src/appV2/Charting/api/schema";
import { PAGE_SIZE } from "@src/appV2/Charting/constants";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { type UseInfiniteQueryResult } from "@tanstack/react-query";
import qs from "qs";
import { z } from "zod";

const chartingCompletionReportsQueryParamsSchema = z.object({
  filter: z.object({
    qualifications: z.string(),
    startDate: z.string(),
    endDate: z.string(),
    workerNameSearch: z.string().optional(),
    chartingNoncomplianceState: z.string().optional(),
  }),
  page: z
    .object({
      // Use coerce because qs.parse returns strings from URL query params
      size: z.coerce.number().optional(),
      cursor: z.string().optional(),
    })
    .optional(),
});

type ChartingCompletionReportsQueryParams = z.input<
  typeof chartingCompletionReportsQueryParamsSchema
>;

interface GetChartingCompletionReportsParams {
  workplaceId: string;
  qualifications: string[];
  startDate: string;
  endDate: string;
  workerNameSearch?: string;
  page?: {
    size?: number;
    cursor?: string;
  };
  chartingNoncomplianceState?: ChartingNoncomplianceState[];
}

export function useGetChartingCompletionReports(
  params: GetChartingCompletionReportsParams,
  options: UseGetQueryOptions<GetChartingCompletionReportsResponse> = {}
) {
  const {
    workplaceId,
    qualifications,
    startDate,
    endDate,
    workerNameSearch,
    page,
    chartingNoncomplianceState,
  } = params;
  const filter: Record<string, string> = {
    qualifications: qualifications.join(","),
    startDate,
    endDate,
    ...(isDefined(workerNameSearch) && { workerNameSearch }),
    ...(isDefined(chartingNoncomplianceState) && {
      chartingNoncomplianceState: chartingNoncomplianceState.join(","),
    }),
  };

  const queryParams: Record<string, unknown> = { filter };
  if (page) {
    queryParams.page = page;
  }

  return useGetQuery({
    url: `${environmentConfig.REACT_APP_BASE_API_URL}/workplaces/${workplaceId}/charting-completion-reports`,
    queryParams,
    responseSchema: GetChartingCompletionReportsResponseSchema,
    meta: { logErrorMessage: APP_V2_APP_EVENTS.GET_CHARTING_COMPLETION_REPORTS_FAILURE },
    ...options,
  });
}

interface NextPageParameter {
  filter: ChartingCompletionReportsQueryParams["filter"];
  page?: { size?: number; cursor?: string };
}

export function useInfiniteGetChartingCompletionReports(
  params: Omit<GetChartingCompletionReportsParams, "page"> & { pageSize?: number },
  options: UseInfiniteGetParsedQueryOptions<typeof GetChartingCompletionReportsResponseSchema> = {}
): UseInfiniteQueryResult<GetChartingCompletionReportsResponse> {
  const {
    workplaceId,
    qualifications,
    startDate,
    endDate,
    workerNameSearch,
    pageSize = PAGE_SIZE,
    chartingNoncomplianceState,
  } = params;

  const queryParams: ChartingCompletionReportsQueryParams = {
    filter: {
      qualifications: qualifications.join(","),
      startDate,
      endDate,
      ...(isDefined(workerNameSearch) && { workerNameSearch }),
      ...(isDefined(chartingNoncomplianceState) && {
        chartingNoncomplianceState: chartingNoncomplianceState.join(","),
      }),
    },
    page: { size: pageSize },
  };

  return useInfiniteGetParsedQuery<
    typeof GetChartingCompletionReportsResponseSchema,
    typeof chartingCompletionReportsQueryParamsSchema,
    NextPageParameter
  >({
    url: `${environmentConfig.REACT_APP_BASE_API_URL}/workplaces/${workplaceId}/charting-completion-reports`,
    queryParams,
    queryParamsSchema: chartingCompletionReportsQueryParamsSchema,
    responseSchema: GetChartingCompletionReportsResponseSchema,
    meta: { logErrorMessage: APP_V2_APP_EVENTS.GET_CHARTING_COMPLETION_REPORTS_FAILURE },
    getNextPageParam: (lastPage): NextPageParameter | undefined => {
      if (!lastPage.links?.next) {
        return undefined;
      }

      try {
        // Parse the URL to extract nested query parameters
        const url = new URL(lastPage.links.next, environmentConfig.REACT_APP_BASE_API_URL);
        // eslint-disable-next-line import/no-named-as-default-member
        return qs.parse(url.search, { ignoreQueryPrefix: true }) as unknown as NextPageParameter;
      } catch {
        return undefined;
      }
    },
    ...options,
  });
}
